import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { Text } from 'foundations-components/components/Text';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import _capitalize from 'hs-lodash/capitalize';
import _get from 'hs-lodash/get';
import { Fragment, useCallback, useContext, useEffect, useMemo } from 'react';
import { ACTIONS, SCREENS } from '../../constants/Tracking';
import { PROPERTIES_VIEWS } from '../../constants/properties';
import { objectTypes, pluralTypeToSingle } from '../../constants/types';
import { AppContext, appActions } from '../../contexts/app/context';
import { PreviewModalPropsContext } from '../../contexts/previewModal/PropsContext';
import useLinkedPropertiesModal from '../../hooks/useLinkedPropertiesModal';
import { generateObjectDirectLink } from '../../utils/urls';
import ApiOutagePlaceholder from '../common/ApiOutagePlaceholder/ApiOutagePlaceholder';
import FormattedCardProperty from '../common/FormattedCardProperty/FormattedCardProperty';
import EditPropertyWrapper from './EditPropertyWrapper/EditPropertyWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function PreviewModal({
  object,
  type,
  closeFunction,
  portalId,
  openObjectDirectLink,
  propertyKeysToShow,
  propertiesDetails,
  isLoading = false,
  isApiOutageError = false
}) {
  const {
    usageTracker,
    extensionComponents
  } = useContext(PreviewModalPropsContext);
  const {
    appName
  } = useContext(AppContext);
  const link = generateObjectDirectLink(portalId, type, object.id);
  const [openLinkedPropsModal, LinkedPropsModal] = useLinkedPropertiesModal();
  const closeHandler = useCallback(() => {
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS[`associated${_capitalize(pluralTypeToSingle[type])}`],
      type: pluralTypeToSingle[type]
    }, ACTIONS.general.closePreview));
    closeFunction();
  }, [closeFunction, type, usageTracker]);
  useEffect(() => {
    appActions.setSkipIntervalRefresh(true);
    return () => {
      appActions.setSkipIntervalRefresh(false);
    };
  }, []);
  const content = useMemo(() => {
    if (isApiOutageError) {
      return /*#__PURE__*/_jsx(ApiOutagePlaceholder, {
        title: I18n.text('home.generalError.description'),
        description: I18n.text('errors.generalHubSpotApiErrorDescription')
      });
    }
    if (isLoading || !object.id) {
      return /*#__PURE__*/_jsx(LoadingOverlay, {
        contextual: true
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [propertyKeysToShow.map(propertyName => /*#__PURE__*/_jsx(EditPropertyWrapper, {
        type: type,
        objectId: object.id,
        propertyName: propertyName,
        currentValue: object.properties && object.properties[propertyName] || '',
        openLinkedPropsModal: openLinkedPropsModal,
        children: /*#__PURE__*/_jsx(FormattedCardProperty, {
          propertyKey: propertyName,
          propertyDetails: _get(propertiesDetails, propertyName),
          value: object.properties && object.properties[propertyName],
          view: PROPERTIES_VIEWS.PREVIEW_MODAL,
          entityType: pluralTypeToSingle[type],
          appName: appName
        })
      }, propertyName)), /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("br", {}), extensionComponents.map((Component, index) => /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx("br", {}), Component]
        }, index))]
      })]
    });
  }, [appName, extensionComponents, isApiOutageError, isLoading, object.id, object.properties, openLinkedPropsModal, propertiesDetails, propertyKeysToShow, type]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ModalPanel, {
      "data-test-id": `${appName}-associations-preview-modal`,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(DialogHeader, {
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h5",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "associationModals.preview.about",
              options: {
                type: pluralTypeToSingle[type]
              }
            })
          }), /*#__PURE__*/_jsx(DialogCloseButton, {
            "data-test-id": `${appName}-associations-preview-modal-close-button`,
            onClick: closeHandler
          })]
        }), /*#__PURE__*/_jsx(DialogBody, {
          children: content
        }), type !== objectTypes.meetings && !isApiOutageError && !isLoading ? /*#__PURE__*/_jsx(DialogFooter, {
          children: /*#__PURE__*/_jsx(TFlex, {
            justify: "center",
            children: /*#__PURE__*/_jsx(Button, {
              use: "primary",
              onClick: e => openObjectDirectLink(e, link, type),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `associationModals.preview.open`
              })
            })
          })
        }) : null]
      })
    }), LinkedPropsModal]
  });
}
export default PreviewModal;